import type { ReactNode } from 'react';
import type {
  ContentTypePropertyConfig,
  ContentTypePropertySchema,
} from '../../../lib/flotiq-client/types/api-response';
import type { UseAppForm } from '../../form';

import { use, useMemo, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { v4 as uuid } from 'uuid';
import * as Y from 'yjs';

import { getDefaultValueForType, getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import HelpErrorTextsTemplate from '../../../components/HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import RequiredTemplate from '../../../components/RequiredTemplate/RequiredTemplate';
import Tooltip from '../../../components/Tooltip/Tooltip';

import ContentObjectFormContext from '../../../contexts/ContentObjectFormContext';
import { useModals } from '../../../contexts/ModalContext';
import WebsocketContext from '../../../contexts/WebsocketContext';

import { CaretUpIcon, QuestionMarkCircleIcon } from '../../../images/shapes';

import { syncYjsMapContent } from '../../collaboration/syncYjs';
import useYjsField from '../../collaboration/useYjsField';
import { useFieldContext, useFormContext } from '../../form';
import { useFieldErrors } from '../../useFieldErrors';

const swapIds = (array: unknown[], oldIdx: number, newIdx: number) => {
  const oldValue = array[oldIdx];
  const newValue = array[newIdx];
  array[oldIdx] = newValue;
  array[newIdx] = oldValue;
};

export interface ListFieldProps {
  /**
   * Field label above items
   */
  label?: ReactNode;
  /**
   * If field is required
   */
  required?: boolean;
  /**
   * List items properties
   */
  itemsProps: Exclude<ContentTypePropertyConfig['items'], undefined>;
  /**
   * List items schema
   */
  itemsSchema: Exclude<ContentTypePropertySchema['items'], undefined>;
  /**
   * Help text under items
   */
  helpText?: ReactNode;
  /**
   * If form is disabled
   */
  disabled?: boolean;
  /**
   * If field is readonly (non-interactive but without disabled classes)
   */
  readonly?: boolean;
  /**
   * If field is rendered in content type definition form
   */
  isCtdForm?: boolean;
  /**
   * Additional container classes
   */
  additionalClasses?: string;
  /**
   * Additional item fields container classes
   */
  itemAdditionalClasses?: string;
  /**
   * Test id for field
   */
  testId?: string;
}

const ListField = ({
  label = '',
  required = false,
  itemsProps,
  itemsSchema,
  helpText,
  disabled = false,
  readonly = false,
  isCtdForm = false,
  additionalClasses,
  itemAdditionalClasses,
  testId,
}: ListFieldProps) => {
  const form = useFormContext() as unknown as UseAppForm;
  const field = useFieldContext<any[]>();
  const error = useFieldErrors(field.name);
  const yjsField = useYjsField('array');
  const { yjsDocument } = use(WebsocketContext);

  const { t } = useTranslation();
  const modal = useModals();

  const [open, toggleOpen] = useReducer((open) => !open, true);

  const { isPatchable } = use(ContentObjectFormContext);
  const value = useMemo(() => field.state.value || [], [field.state.value]);

  const arrayIdsRefs = useRef(value.map(() => uuid()));

  const addProperty = () => {
    const newObject: Record<string, any> = {};
    itemsProps.order.forEach((key) => {
      const schemaProperty = itemsSchema.properties?.[key];
      newObject[key] =
        schemaProperty && 'default' in schemaProperty && schemaProperty?.default
          ? schemaProperty?.default
          : getDefaultValueForType(schemaProperty?.type || '');
    });

    if (yjsDocument) {
      yjsDocument.transact(() => {
        const newItemYjsMap = new Y.Map<any>();
        yjsField.insert(newItemYjsMap);
        syncYjsMapContent(
          newItemYjsMap,
          newObject,
          itemsSchema.properties,
          itemsProps.propertiesConfig,
        );
      });
    }

    field.pushValue(newObject);
    arrayIdsRefs.current.push(uuid());
  };

  const renderListField = (key: string, itemName: string) => {
    const itemProps = itemsProps.propertiesConfig[itemName];
    const itemSchema = itemsSchema.properties?.[itemName];
    const isRequired =
      !isPatchable &&
      (itemsSchema.required?.findIndex((key) => key === itemName) ?? -1) > -1;

    const label =
      isCtdForm && itemProps.hidden ? (
        <div className="inline-flex items-center gap-1">
          {itemProps.label || itemName}
          <Tooltip
            tooltip={t('PropertyForm.HiddenListPropTooltip')}
            tooltipPlacement="topLeft"
            phoneTooltipPlacement="topLeft"
          >
            <QuestionMarkCircleIcon className="w-3 text-blue" />
          </Tooltip>
        </div>
      ) : null;

    return !itemProps || !itemSchema ? (
      <></>
    ) : (
      <form.CtoCustomField
        key={key}
        name={key}
        properties={itemProps}
        schema={itemSchema}
        isRequired={isRequired}
        disabled={disabled}
        readonly={readonly}
        label={label}
        additionalClasses="max-w-3xl"
        isCtdForm={isCtdForm}
        testId={testId}
      />
    );
  };

  const handleOrderUp = (idx: number) => {
    field.swapValues(idx - 1, idx);
    swapIds(arrayIdsRefs.current, idx, idx - 1);
    yjsField.swap(idx, idx - 1);
  };

  const handleOrderDown = (idx: number) => {
    field.swapValues(idx + 1, idx);
    swapIds(arrayIdsRefs.current, idx, idx + 1);
    yjsField.swap(idx + 1, idx);
  };

  const handleDeleteItem = async (idx: number) => {
    const result = await modal.delete(t('ContentForm.RemoveItem'));
    if (!result) return;

    field.removeValue(idx);
    arrayIdsRefs.current.splice(idx, 1);
    yjsField.delete(idx);
  };

  return (
    <div
      className={twMerge(
        'bg-white dark:bg-transparent px-0',
        additionalClasses,
      )}
    >
      <label
        className="block w-full text-sm text-slate-400 dark:text-slate-200 pb-2.5"
        {...getTestProps(testId, 'label')}
      >
        {label}
        {required && <RequiredTemplate />}
        {isCtdForm && (
          <Button
            onClick={toggleOpen}
            buttonColor="borderless"
            iconImage={
              <CaretUpIcon
                className={twMerge(
                  'w-2 rotate-180 text-blue',
                  open ? 'rotate-0' : 'rotate-180',
                )}
                {...getTestProps(testId, 'list-default-open')}
              />
            }
            additionalIconClasses="ml-1"
            additionalClasses="mr-2"
          >
            {t('ContentTypeForm.ShowProperties')}
          </Button>
        )}
      </label>
      <div
        className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-2.5
        md:p-5 rounded-lg"
      >
        <div className="relative">
          {open && value?.length > 0 && (
            <div className="mb-2 space-y-2">
              {value.map((_, idx, currentValue) => {
                const fieldName = `${field.name}[${idx}]`;
                return (
                  <form.AppField key={fieldName} name={fieldName}>
                    {(subField) => (
                      <subField.ListFieldItem
                        key={arrayIdsRefs.current[idx]}
                        label={label}
                        itemsProps={itemsProps}
                        currentValue={currentValue}
                        idx={idx}
                        renderListField={renderListField}
                        handleOrderUp={handleOrderUp}
                        handleOrderDown={handleOrderDown}
                        handleDeleteItem={handleDeleteItem}
                        disabled={disabled || readonly}
                        isCtdForm={isCtdForm}
                        additionalClasses={itemAdditionalClasses}
                        {...getTestProps(testId, fieldName, 'testId')}
                      />
                    )}
                  </form.AppField>
                );
              })}
            </div>
          )}
          {(!isCtdForm || !value.length) && (
            <Button
              onClick={addProperty}
              buttonColor="blueBordered"
              disabled={disabled || readonly}
              {...getTestProps(testId, 'add-item', 'testId')}
            >
              {t('ContentForm.AddItem')}
            </Button>
          )}
        </div>
        <HelpErrorTextsTemplate
          helpText={helpText}
          error={typeof error === 'string' ? error : ''}
          testId={testId}
        />
      </div>
    </div>
  );
};

export default ListField;
